<template>
  <div class="card mb-25 border-0 rounded-0 bg-white letter-spacing">
    <div class="card-body p-15 p-sm-20 p-md-25">
      <h2 class="fs-4 text-black fw-semibold mb-20">Détails de la session</h2>
      
      <div v-if="isLoading" class="text-center">
        <p class="mb-10">Chargement...</p>
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Chargement...</span>
        </div>
      </div>
      
      <div v-else-if="session">
        <div class="row mb-20">
          <div class="col-md-6">
            <p><strong>Nombre de questions :</strong> {{ session.count }}</p>
            
            <p><strong>Date de création :</strong> {{ format_date(session.createdAt) }}</p>
          </div>
        </div>
        
        <h4 class="mb-10">Questions associées</h4>
        <div class="table-responsive">
          <table class="table text-nowrap align-middle mb-0">
            <thead>
              <tr>
                <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                  Contenu
                </th>
                <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                  Type
                </th>
                <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                  Propositions
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(question, index) in questions" :key="index">
                <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                  {{ question.contenu }}
                </td>
                <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                  {{ question.type === 'choix_multiple' ? 'Choix multiple' : question.type === 'oui_non' ? 'Oui/Non' : 'Texte libre' }}
                </td>
                <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                  <ul class="mb-0">
                    <li v-for="(prop, propIndex) in question.propositions" :key="propIndex">
                      {{ prop.contenu }} {{ prop.estLaBonneReponse ? '(Correcte)' : '' }}
                    </li>
                  </ul>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div class="d-flex justify-content-center gap-2 mt-20">
          <router-link to="/liste-sessions" class="btn btn-primary">
            <i class="fa fa-arrow-left me-1"></i>
            Retour
          </router-link>
        </div>
      </div>
      
      <div v-else class="alert alert-danger text-center">
        <h4>Session non trouvée</h4>
        <p>Aucune session ne correspond à cet identifiant.</p>
        <router-link to="/liste-sessions" class="btn btn-primary">
          Retour à la liste
        </router-link>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import ApiService from '@/services/ApiService';
import { format_date, error } from '@/utils/utils';

interface Session {
  id: number;
  count: number;
  filiereNiveauMatiere: { id: number; titre: string } | null;
  createdAt: Date;
}

interface Question {
  id: number;
  contenu: string;
  type: string;
  propositions: Array<{ id: number; contenu: string; estLaBonneReponse: boolean }>;
}

export default defineComponent({
  name: 'ViewSessionPage',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const session = ref<Session | null>(null);
    const questions = ref<Question[]>([]);
    const isLoading = ref(true);

    const loadSession = async () => {
      try {
        isLoading.value = true;
        const sessionId = route.params.id;
        const { data } = await ApiService.get(`/sessions/${sessionId}`);
        if (data.code === 200 && data.data) {
          session.value = data.data;
          const questionsResponse = await ApiService.get(`/sessions/${sessionId}/questions`);
          if (questionsResponse.data.code === 200) {
            questions.value = questionsResponse.data.data;
          }
        } else {
          throw new Error('Session non trouvée');
        }
      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur lors du chargement de la session');
      } finally {
        isLoading.value = false;
      }
    };

    onMounted(() => {
      loadSession();
    });

    return {
      session,
      questions,
      isLoading,
      format_date,
    };
  },
});
</script>

<style scoped>
.card {
  border-radius: 0;
}

.text-black {
  color: #000;
}

.text-danger {
  font-size: 0.875rem;
}

.btn-primary {
  border-radius: 0;
  transition: all 0.2s ease;
}

.btn:hover {
  filter: brightness(90%);
}

.mb-10 {
  margin-bottom: 10px;
}

.mb-20 {
  margin-bottom: 20px;
}

.mt-20 {
  margin-top: 20px;
}

.fs-4 {
  font-size: 1.5rem;
}

.alert {
  border-radius: 0;
}

.spinner-border {
  width: 2rem;
  height: 2rem;
}
</style>